import { Box, List, ListItemButton, ListItemText, TextField, Typography } from "@mui/material"
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import { InventoryCheckUpdateUser, InventoryCheckUser } from "../entities/InventoryCheck"
import { useApi } from "../../../hooks/useApi"
import { getUserAvailable, updateInventoryCheckDetailUser } from "../api"
import { Loading } from "../../../components/UI/Loading/Loading"
import { Status } from "../../../hooks/useFetchState"

interface ReassignInventoryCheckModalProps {
  inventoryCheckDetailId: number
  inventoryCheckId: number
  handleCloseModal: () => void
  handleSnackbar: (message: string, success: boolean) => void
  requestInventoryCheckDetail: (inventoryCheckId: number) => void
}

export interface ChangeUserInventoryCheckModalRef {
  submit: () => void
}

export const ChangeUserInventoryCheckModal = forwardRef<
  ChangeUserInventoryCheckModalRef,
  ReassignInventoryCheckModalProps
>(({
  inventoryCheckDetailId,
  inventoryCheckId,
  handleCloseModal,
  handleSnackbar,
  requestInventoryCheckDetail
}, ref) => {

  const [loading, setLoading] = useState<boolean>(false)
  const { state, callApi } = useApi<InventoryCheckUser[]>()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [searchText, setSearchText] = useState<string>('')

  const requestGetUserList = () => {
    setLoading(true)
    callApi(getUserAvailable(inventoryCheckDetailId))
      .then(() => { setLoading(false) })
      .catch((error) => { handleSnackbar(error, false) })
    setLoading(false)
  }

  useEffect(() => {
    requestGetUserList()
  }, [])

  const filteredItems = (state.data || []).filter((item) =>
    item.username.toLowerCase().includes(searchText.toLowerCase())
  )

  const handleSelect = (id: number) => {
    setSelectedId(id)
  }

  const requestUpdateUser = () => {
    if (selectedId !== null) {
      const userDto: InventoryCheckUpdateUser = { userId: selectedId }

      setLoading(true)
      callApi(updateInventoryCheckDetailUser(inventoryCheckDetailId, userDto))
        .then(() => {
          handleSnackbar('Usuario actualizado correctamente', true)
          requestInventoryCheckDetail(inventoryCheckId)
        })
        .catch((error) => {
          handleSnackbar(error, false)
        })
        .finally(() => {
          setLoading(false)
          handleCloseModal()
        })
    }
  }

  useImperativeHandle(ref, () => ({
    submit: () => {
      requestUpdateUser()
    }
  }))

  if (state.status === Status.Loading || loading) {
    return (
      <div>
        <Typography>
          <Loading />
        </Typography>
      </div>
    )
  }

  return (
    <Box style={{ borderRadius: 8, backgroundColor: '#f5f5f5', padding: '0 16px 12px' }}>
      <Typography variant='overline' style={{ fontWeight: 700, letterSpacing: 1.2, color: '#1976d2', display: 'block', marginBottom: 8 }}>
        Seleccionar Usuario
      </Typography>

      <TextField
        fullWidth
        variant='outlined'
        size='small'
        placeholder='Buscar usuario...'
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 12, backgroundColor: '#fff', borderRadius: 4 }}
      />

      <List style={{ maxHeight: '220px', overflowY: 'auto' }}>
        {filteredItems.map((item) => (
          <ListItemButton
            key={item.userId}
            selected={selectedId === item.userId}
            onClick={() => handleSelect(item.userId)}
            style={{
              border: '1px solid #e0e0e0',
              margin: '2px 0',
              borderRadius: 6,
              padding: '4px 16px',
              color: selectedId === item.userId ? 'white' : 'black',
              backgroundColor: selectedId === item.userId ? '#1976d2' : 'white',
            }}
          >
            <ListItemText primary={item.username} />
          </ListItemButton>
        ))}
        {filteredItems.length === 0 && (
          <Typography variant='body2' color='textSecondary' align='center'>
            No se encontraron usuarios.
          </Typography>
        )}
      </List>
    </Box>
  )
})
